import React, { FunctionComponent } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { Form } from 'model/Forms';
import FormsTableRow from './FormsTableRow';
import Spinner from 'components/UI/Spinner';
import styles from './FormsTable.module.scss';
import FormsIsEmpty from './FormsIsEmpty';
import { useTimeZone } from 'hooks';

interface FormsTableProps {
    filteredForms: Form[];
    onSelectForm: (form: Form) => void;
    onFormDeleted?: (formId: number) => void;
    onFormEdit?: (form: Form) => void;
    onFormUpdated?: () => void;
    isLoading?: boolean;
    isEmpty?: boolean;
    className?: string;
    pagination?: {
        page: number;
        totalPages: number;
        size?: number;
    };
    onPageChange?: (page: number) => void;
    onSizeChange?: (size: number) => void;
    onOpenShareSendClients?: (formId?: number) => void;
    // Optional current search/filter text to customize empty messaging
    searchText?: string;
    emptyTitleDefault?: string;
    emptySubtitleDefault?: string;
    emptyTitleFiltered?: string;
    emptySubtitleFiltered?: string;
}

const FormsTable: FunctionComponent<FormsTableProps> = props => {
    const isMobile = useMediaQuery({ query: '(max-width:768px)' });
    const isTabletOrBelow = useMediaQuery({ query: '(max-width:960px)' });
    // We render the full column set in all viewports
    const colSpan = 6;
    const showHeader = !isMobile || (!props.isEmpty && !props.isLoading);
    const timeZone = useTimeZone();

    const renderTableContent = () => {
        // Show spinner when loading
        if (props.isLoading) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={colSpan}
                            style={{ textAlign: 'center', padding: '40px' }}
                        >
                            <Spinner />
                        </td>
                    </tr>
                </TableBody>
            );
        }

        // No results for current filters/search
        if (props.isEmpty) {
            const isFiltered = !!props.searchText && props.searchText.trim().length > 0;
            const title = isFiltered
                ? props.emptyTitleFiltered || 'Oops! No result found.'
                : props.emptyTitleDefault || 'No forms yet';
            const subtitle = isFiltered
                ? props.emptySubtitleFiltered || 'Try adjusting your search or filters.'
                : props.emptySubtitleDefault || 'Add your first form to start...';
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={colSpan}
                            className={styles.emptyCell}
                        >
                            <div className={styles.emptyCellContent}>
                                <FormsIsEmpty
                                    title={title}
                                    text={subtitle}
                                />
                            </div>
                        </td>
                    </tr>
                </TableBody>
            );
        }

        // Normal data
        return (
            <TableBody>
                {props.filteredForms.map((form, index) => (
                    <FormsTableRow
                        key={form.id || index}
                        form={form}
                        onEdit={() => props.onSelectForm(form)}
                        onDeleted={() => props.onFormDeleted && props.onFormDeleted(form.id!)}
                        onMenuEdit={props.onFormEdit}
                        onUpdated={props.onFormUpdated}
                        onOpenShareSendClients={props.onOpenShareSendClients}
                        isLast={props.filteredForms.length === index + 1}
                        isMobile={isMobile}
                        timezone={timeZone}
                    />
                ))}
            </TableBody>
        );
    };

    return (
        <Table
            className={[styles.table, props.className].filter(Boolean).join(' ')}
            fullHeight={true}
            firstColumnFixed={isTabletOrBelow}
            pagination={props.pagination}
            onPageChange={props.onPageChange}
            onSizeChange={props.onSizeChange}
        >
            {showHeader && (
                <TableHeader className={styles.tableHeader}>
                    <TableHeaderCell>Name</TableHeaderCell>
                    <TableHeaderCell>Created On</TableHeaderCell>
                    <TableHeaderCell>Updated At</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: 'right' }}>Sent</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: 'right' }}>Not Completed</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                </TableHeader>
            )}
            {renderTableContent()}
        </Table>
    );
};

export default FormsTable;
